import { useForm } from '@tanstack/react-form';
import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { z, ZodError } from 'zod';

import {
  AUTOFILL_SUBMIT_DELAYS_MS,
  AUTOFILL_WAIT_MS,
  getLoginFormCredentials,
  hasLoginCredentials,
  LOGIN_PASSWORD_FIELD,
  LOGIN_USERNAME_FIELD,
  scheduleAfterDelays
} from './utils';
import styles from '../styles.module.css';
import Button from '@/components/LoginComponents/Button';
import Input from '@/components/LoginComponents/Input';
import LinkButton from '@/components/LoginComponents/Link';
import { POOL_NAME } from '@/constants';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { startPresignupRecoveryLogout } from '@/oidcConfig';
import {
  changeUserPassword,
  clearStatus,
  signInAndFill,
  signOidcAndFill,
  signOut
} from '@/slices/authSlice';
import {
  clearOidcPresignupRecoveryFlags,
  clearOidcPresignupSsoRetryPending,
  getOidcErrorMessage,
  getOidcPresignupErrorMessage,
  hasOidcPresignupLogoutAttempted,
  isOidcPresignupError,
  isOidcPresignupSsoRetryPending,
  setOidcPresignupLogoutAttempted,
  setOidcPresignupSsoRetryPending
} from '@/solaris-lens-common-lib-web/src/utils/oidcLogin';
import { RootState } from '@/store';

const loginSchema = z.object({
  username: z.string().min(1, 'Username should not be empty'),
  password: z.string().min(1, 'Password should not be empty')
});

const SSO_LOADING_TIMEOUT_MS = 15000;
const SSO_FORCE_LOGIN_ARGS = {
  prompt: 'login'
} as const;

/**
 * Returns true when the OAuth redirect URL includes an Azure/Cognito error.
 * @param {string} search Location search string
 * @returns {boolean}
 */
const hasOidcRedirectError = (search: string) =>
  Boolean(getOidcErrorMessage(search));

function LoginContainer() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isPresignupRecoveryLoading, setIsPresignupRecoveryLoading] =
    useState(false);
  const [isSsoErrorCleanupLoading, setIsSsoErrorCleanupLoading] =
    useState(false);
  const [oidcPresignupError, setOidcPresignupError] = useState('');
  const [oidcUrlError, setOidcUrlError] = useState('');
  const authState = useSelector((state: RootState) => state.auth);
  const errorResponse = authState?.error?.description;
  const displayErrorResponse =
    errorResponse || oidcPresignupError || oidcUrlError;
  const presignupRecoveryStartedRef = useRef(false);
  const presignupSsoRetryStartedRef = useRef(false);
  const ssoErrorCleanupStartedRef = useRef(false);
  const loginFormRef = useRef<HTMLFormElement>(null);
  const loginSubmitLockRef = useRef(false);
  const isLoadingRef = useRef(false);
  const autofillTimeoutsRef = useRef<number[]>([]);
  const waitingForAutofillRef = useRef(false);

  const form = useForm({
    defaultValues: {
      username: '',
      password: ''
    },
    validators: {
      onSubmit: loginSchema
    },
    onSubmit: async ({ value }) => {
      setIsLoading(true);
      await dispatch(
        signInAndFill({
          username: value.username,
          password: value.password,
          poolName: POOL_NAME
        })
      );
      setIsLoading(false);
    }
  });

  isLoadingRef.current = isLoading;

  const clearAutofillTimeouts = useCallback(() => {
    autofillTimeoutsRef.current.forEach(timeoutId => {
      window.clearTimeout(timeoutId);
    });
    autofillTimeoutsRef.current = [];
  }, []);

  const submitFilledLogin = useCallback(
    async (showErrorsWhenEmpty = false) => {
      if (loginSubmitLockRef.current || isLoadingRef.current) {
        return;
      }

      const formElement = loginFormRef.current;
      if (!formElement) {
        return;
      }

      const credentials = getLoginFormCredentials(formElement);

      if (!hasLoginCredentials(credentials)) {
        if (showErrorsWhenEmpty) {
          form.setFieldValue(LOGIN_USERNAME_FIELD, credentials.username);
          form.setFieldValue(LOGIN_PASSWORD_FIELD, credentials.password);
          await form.handleSubmit();
        }
        return;
      }

      form.setFieldValue(LOGIN_USERNAME_FIELD, credentials.username);
      form.setFieldValue(LOGIN_PASSWORD_FIELD, credentials.password);

      loginSubmitLockRef.current = true;
      waitingForAutofillRef.current = false;
      clearAutofillTimeouts();
      setIsLoading(true);
      try {
        await dispatch(
          signInAndFill({
            username: credentials.username,
            password: credentials.password,
            poolName: POOL_NAME
          })
        );
      } finally {
        setIsLoading(false);
        loginSubmitLockRef.current = false;
      }
    },
    [clearAutofillTimeouts, dispatch, form]
  );

  const scheduleAutofillLogin = useCallback(() => {
    waitingForAutofillRef.current = true;
    clearAutofillTimeouts();
    autofillTimeoutsRef.current = [
      ...scheduleAfterDelays(AUTOFILL_SUBMIT_DELAYS_MS, () => {
        void submitFilledLogin(false);
      }),
      window.setTimeout(() => {
        waitingForAutofillRef.current = false;
      }, AUTOFILL_WAIT_MS)
    ];
  }, [clearAutofillTimeouts, submitFilledLogin]);

  const handleLoginKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return;
      }
      scheduleAutofillLogin();
    },
    [scheduleAutofillLogin]
  );

  const handleLoginFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const credentials = getLoginFormCredentials(event.currentTarget);
      if (hasLoginCredentials(credentials)) {
        void submitFilledLogin(true);
        return;
      }
      scheduleAutofillLogin();
    },
    [scheduleAutofillLogin, submitFilledLogin]
  );

  const handleAutofillFieldChange = useCallback(
    (fieldChange: (value: string) => void, value: string) => {
      fieldChange(value || '');
      if (waitingForAutofillRef.current) {
        void submitFilledLogin(false);
      }
    },
    [submitFilledLogin]
  );

  useEffect(() => {
    return () => {
      clearAutofillTimeouts();
    };
  }, [clearAutofillTimeouts]);

  const auth = useAuth();
  const ssoButtonLoading =
    auth.isLoading || isPresignupRecoveryLoading || isSsoErrorCleanupLoading;

  const onOidcLogin = () => {
    setOidcPresignupError('');
    setOidcUrlError('');
    clearOidcPresignupRecoveryFlags();
    auth.signinRedirect(SSO_FORCE_LOGIN_ARGS);
  };

  /**
   * Clears local OIDC/session state and opens Microsoft logout so the next
   * SSO attempt cannot reuse a stale Azure cookie session.
   */
  const runFederatedSsoCleanup = useCallback(
    async (options: { retrySsoAfterLogout: boolean }) => {
      setIsPresignupRecoveryLoading(true);
      setOidcPresignupLogoutAttempted();
      if (options.retrySsoAfterLogout) {
        setOidcPresignupSsoRetryPending();
      }

      const idToken = auth.user?.id_token;
      const email = auth.user?.profile?.email as string | undefined;

      dispatch(signOut());
      localStorage.clear();
      await Promise.allSettled([auth.removeUser(), auth.clearStaleState()]);

      startPresignupRecoveryLogout(idToken, email);
    },
    [auth, dispatch]
  );

  useEffect(() => {
    if (authState?.accessToken && !authState?.isOidcClient) {
      return;
    }

    const redirectError = getOidcErrorMessage(window.location.search);
    if (redirectError || auth.error) {
      return;
    }

    if (
      auth.isAuthenticated &&
      auth?.user &&
      auth?.error?.message !== 'invalid_grant'
    ) {
      clearOidcPresignupRecoveryFlags();
      setOidcPresignupError('');
      setOidcUrlError('');
      dispatch(
        signOidcAndFill({
          ...auth.user,
          accessToken: auth.user.access_token ?? '',
          refreshToken: auth.user.refresh_token ?? '',
          tokenType: auth.user.token_type ?? '',
          idToken: auth.user.id_token ?? '',
          expiresIn: auth.user.expires_at ?? 0,
          isOidcClient: true,
          poolName: POOL_NAME
        })
      );
    }
  }, [
    auth.error,
    auth.isAuthenticated,
    auth?.user,
    authState?.accessToken,
    authState?.isOidcClient,
    dispatch
  ]);

  useEffect(() => {
    if (authState?.accessToken && !authState?.isOidcClient) {
      return;
    }

    dispatch(clearStatus());
  }, [dispatch, authState?.accessToken, authState?.isOidcClient]);

  useEffect(() => {
    if (
      authState?.accessToken &&
      authState?.username &&
      !authState?.isOidcClient
    ) {
      navigate('/', { replace: true });
    }
  }, [
    authState?.accessToken,
    authState?.username,
    authState?.isOidcClient,
    navigate
  ]);

  useEffect(() => {
    const search = window.location.search;

    if (isOidcPresignupError(search)) {
      setOidcUrlError('');

      if (hasOidcPresignupLogoutAttempted()) {
        setOidcPresignupError(getOidcPresignupErrorMessage(search));
        return;
      }

      if (presignupRecoveryStartedRef.current) return;
      if (auth.isLoading || auth.activeNavigator) return;

      presignupRecoveryStartedRef.current = true;
      void runFederatedSsoCleanup({ retrySsoAfterLogout: true });
      return;
    }

    setOidcPresignupError('');

    if (!hasOidcRedirectError(search) && !auth.error) {
      setOidcUrlError('');
      ssoErrorCleanupStartedRef.current = false;
      return;
    }

    const message =
      getOidcErrorMessage(search) ||
      auth.error?.message ||
      'Single sign-on failed. Please try again.';
    setOidcUrlError(message);

    if (hasOidcPresignupLogoutAttempted()) {
      setIsSsoErrorCleanupLoading(false);
      return;
    }

    if (ssoErrorCleanupStartedRef.current) return;
    if (auth.isLoading || auth.activeNavigator) return;

    ssoErrorCleanupStartedRef.current = true;
    setIsSsoErrorCleanupLoading(true);

    void (async () => {
      setOidcPresignupLogoutAttempted();
      dispatch(signOut());
      localStorage.clear();
      await Promise.allSettled([auth.removeUser(), auth.clearStaleState()]);

      const idToken = auth.user?.id_token;
      const email = auth.user?.profile?.email as string | undefined;
      if (idToken || email) {
        startPresignupRecoveryLogout(idToken, email);
        return;
      }

      setIsSsoErrorCleanupLoading(false);
    })();
  }, [
    auth,
    auth.activeNavigator,
    auth.error,
    auth.isLoading,
    dispatch,
    runFederatedSsoCleanup
  ]);

  useEffect(() => {
    if (!isOidcPresignupSsoRetryPending()) return;
    if (presignupSsoRetryStartedRef.current) return;
    if (isOidcPresignupError(window.location.search)) return;
    if (hasOidcRedirectError(window.location.search)) return;
    if (auth.isLoading || auth.activeNavigator) return;

    presignupSsoRetryStartedRef.current = true;
    clearOidcPresignupSsoRetryPending();
    auth.signinRedirect(SSO_FORCE_LOGIN_ARGS);
  }, [auth, auth.activeNavigator, auth.isLoading]);

  useEffect(() => {
    if (!auth.isLoading) return;

    const timeoutId = window.setTimeout(() => {
      void auth.removeUser();
      void auth.clearStaleState();
      setIsSsoErrorCleanupLoading(false);
      setIsPresignupRecoveryLoading(false);
      setOidcUrlError(
        prev => prev || 'Single sign-on timed out. Please try again.'
      );
    }, SSO_LOADING_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [auth]);

  if (authState?.error?.code === 1003) {
    dispatch(
      changeUserPassword({
        ...authState,
        data: {
          username: form.getFieldValue('username') || '',
          password: form.getFieldValue('password') || ''
        }
      })
    );
    navigate('/change-password');
  }

  return (
    <div className={styles.formWrapper}>
      {displayErrorResponse && (
        <div className={styles.formErrors}>{displayErrorResponse}</div>
      )}
      <form
        ref={loginFormRef}
        className={styles.form}
        autoComplete="on"
        onSubmit={handleLoginFormSubmit}
      >
        <h3>Login</h3>
        <form.Field name="username">
          {field => (
            <Input
              id="loginUsernameInput"
              name={LOGIN_USERNAME_FIELD}
              autoComplete="username"
              type="text"
              placeholder="Email / Username"
              value={field.state.value}
              onChange={value =>
                handleAutofillFieldChange(field.handleChange, value)
              }
              onKeyDown={handleLoginKeyDown}
              errors={field?.state?.meta?.errors as ZodError[]}
            />
          )}
        </form.Field>

        <form.Field name="password">
          {field => (
            <Input
              id="loginPasswordInput"
              name={LOGIN_PASSWORD_FIELD}
              autoComplete="current-password"
              type="password"
              placeholder="Password"
              value={field.state.value}
              onChange={value =>
                handleAutofillFieldChange(field.handleChange, value)
              }
              onKeyDown={handleLoginKeyDown}
              errors={
                (field?.state?.meta?.errors as ZodError[]) || [
                  displayErrorResponse
                ]
              }
            />
          )}
        </form.Field>

        <div className={styles.buttonContainer}>
          <Button
            id="loginSubmitButton"
            type="button"
            label="SUBMIT"
            isLoading={isLoading}
            onClick={() => {
              void submitFilledLogin(true);
            }}
          />
          <LinkButton
            id="loginForgotPasswordLink"
            label="Forgot Password?"
            to="/forgot-password"
          />
        </div>

        <div className={styles.separator}>
          <div className={styles.separatorLine}></div>
          <p className={styles.separatorText}>OR</p>
          <div className={styles.separatorLine}></div>
        </div>

        <Button
          id="loginSsoButton"
          type="button"
          label="LOG IN WITH SSO"
          isLoading={ssoButtonLoading}
          disabled={ssoButtonLoading}
          onClick={() => onOidcLogin()}
        />
      </form>
    </div>
  );
}

export default LoginContainer;
